import ExcelJS from 'exceljs';
import type { PaintRegionAnalyticsByStructuresQuery } from '@/__generated__/graphql';
import { UnitSystemEnum } from '@/__generated__/graphql';
import { convertSqMetersToSqFeet } from '@/utils/unitSystem';
import {
  CM_CALIBRATED_FIELDS,
  CM_REMEDIATION_FIELDS,
  CM_UNCALIBRATED_FIELDS,
  type CmField,
} from './cmFields';
import { COMBINED_HEADER_COLORS } from './headerColors';
import { PSIM_BASE_FIELDS, PSIM_REMEDIATION_FIELDS } from './psimFields';
import { type CellValue, normalizeCellValue } from './exportUtils';

type AnalyticsEntry =
  PaintRegionAnalyticsByStructuresQuery['paintRegionAnalyticsByStructures'][number];
type PsimEntry = NonNullable<AnalyticsEntry['psim']>['baseline'][number];

type CombinedRow = {
  paintRegionName: string;
  cofCategory: string;
  baseline?: PsimEntry;
  remediation?: PsimEntry;
  cm: Record<string, CellValue>;
};

const getCofCategory = (entry?: PsimEntry): string => (entry?.cofCategory ?? '').toString();

const getPsimValue = (entry: PsimEntry | undefined, key: string): CellValue => {
  if (!entry) return '';
  return (entry as Record<string, CellValue>)[key] ?? '';
};

const formatAreaValue = (value: CellValue, unitSystem: UnitSystemEnum): CellValue => {
  const normalized = normalizeCellValue(value);
  if (normalized === '' || normalized === null || normalized === undefined) return '';
  const numericValue = typeof normalized === 'number' ? normalized : Number(normalized);
  if (Number.isNaN(numericValue)) return normalized;

  if (unitSystem === UnitSystemEnum.Imperial) {
    return convertSqMetersToSqFeet(numericValue);
  }

  return numericValue;
};

const formatFieldValue = (
  field: { subLabel: string },
  value: CellValue,
  unitSystem: UnitSystemEnum
): CellValue =>
  field.subLabel === 'Area' ? formatAreaValue(value, unitSystem) : normalizeCellValue(value);

const getSubLabel = (subLabel: string, areaUnitLabel: string) =>
  subLabel === 'Area' ? `Area (${areaUnitLabel})` : subLabel;

const buildRows = (
  analytics: PaintRegionAnalyticsByStructuresQuery['paintRegionAnalyticsByStructures']
): CombinedRow[] => {
  const rows: CombinedRow[] = [];

  analytics.forEach((entry: AnalyticsEntry) => {
    const paintRegionName = entry.paintRegionName ?? '';
    const cm = (entry.cm ?? {}) as Record<string, CellValue>;
    const psim = entry.psim;
    const baseline = psim?.baseline ?? [];
    const remediation = psim?.remediation ?? [];
    const rowMap = new Map<string, CombinedRow>();

    const getRow = (cofCategory: string) => {
      const key = `${paintRegionName}__${cofCategory}`;
      const existing = rowMap.get(key);
      if (existing) return existing;
      const row: CombinedRow = { paintRegionName, cofCategory, cm };
      rowMap.set(key, row);
      rows.push(row);
      return row;
    };

    baseline.forEach((item: PsimEntry) => {
      const row = getRow(getCofCategory(item));
      row.baseline = item;
    });

    remediation.forEach((item: PsimEntry) => {
      const row = getRow(getCofCategory(item));
      row.remediation = item;
    });

    if (baseline.length === 0 && remediation.length === 0) {
      rows.push({ paintRegionName, cofCategory: '', cm });
    }
  });

  return rows;
};

export const buildCombinedWorkbook = (
  analytics: PaintRegionAnalyticsByStructuresQuery['paintRegionAnalyticsByStructures'],
  unitSystem: UnitSystemEnum
) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Paint Region Analytics');
  const rows = buildRows(analytics);
  const areaUnitLabel = unitSystem === UnitSystemEnum.Imperial ? 'ft²' : 'm²';

  // Create flat headers - single row with fully qualified column names
  const headers: string[] = [
    'Paint Block',
    'PSIM COF',
    `PSIM Block Area (${areaUnitLabel})`,
    ...PSIM_BASE_FIELDS.map(
      (field) => `PSIM Base ${field.label} ${getSubLabel(field.subLabel, areaUnitLabel)}`
    ),
    ...CM_UNCALIBRATED_FIELDS.map(
      (field) =>
        `CM Base (Uncalibrated) ${field.label} ${getSubLabel(field.subLabel, areaUnitLabel)}`
    ),
    ...CM_CALIBRATED_FIELDS.map(
      (field) => `CM Base (Calibrated) ${field.label} ${getSubLabel(field.subLabel, areaUnitLabel)}`
    ),
    `PSIM Remediated Area through Chase or Block Work (${areaUnitLabel})`,
    `PSIM Chase Remediated Area (${areaUnitLabel})`,
    `CM Remediation Area (${areaUnitLabel}) (Input by User)`,
    `CM Remediated Chase Area (${areaUnitLabel})`,
    `CM Remediation Area in Fabric (${areaUnitLabel})`,
    ...PSIM_REMEDIATION_FIELDS.map(
      (field) => `PSIM Remediation ${field.label} ${getSubLabel(field.subLabel, areaUnitLabel)}`
    ),
    ...CM_REMEDIATION_FIELDS.map(
      (field) =>
        `CM Remediation (Calibrated) ${field.label} ${getSubLabel(field.subLabel, areaUnitLabel)}`
    ),
  ];

  // Add header row with styling
  const headerRow = worksheet.addRow(headers);
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COMBINED_HEADER_COLORS.paintBlock },
    };
    cell.font = {
      bold: true,
      name: 'Arial',
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
  });

  worksheet.getRow(1).height = 30;

  // Add data rows - Paint Block name repeats in each row (no merging)
  // CM values only appear in the first row for each Paint Block
  let lastPaintRegionName = '';
  rows.forEach((row) => {
    const psimEntry = row.baseline ?? row.remediation;
    const isFirstRowForPaintBlock = row.paintRegionName !== lastPaintRegionName;
    lastPaintRegionName = row.paintRegionName;

    // Determine if this is a PSIM or CM deployment based on presence of PSIM data
    const hasPsimData = !!psimEntry;

    // Only include CM data in the first row for each Paint Block
    const actualTotalRemediatedArea = isFirstRowForPaintBlock
      ? (row.cm['actual_total_remediated_area'] ?? row.cm['actualTotalRemediatedArea'])
      : '';
    const remediatedChaseArea = isFirstRowForPaintBlock
      ? (row.cm['remediated_chase_area'] ?? row.cm['remediatedChaseArea'])
      : '';
    const totalAlreadyCompleted = isFirstRowForPaintBlock
      ? (row.cm['total_already_completed'] ?? row.cm['totalAlreadyCompleted'])
      : '';

    const values: CellValue[] = [
      row.paintRegionName, // Repeats for each row instead of being merged
      row.cofCategory,
      formatAreaValue(getPsimValue(psimEntry, 'area'), unitSystem),
      ...PSIM_BASE_FIELDS.map((field) =>
        formatFieldValue(field, getPsimValue(row.baseline, field.key), unitSystem)
      ),
      ...CM_UNCALIBRATED_FIELDS.map((field: CmField) =>
        isFirstRowForPaintBlock ? formatFieldValue(field, row.cm[field.key], unitSystem) : ''
      ),
      ...CM_CALIBRATED_FIELDS.map((field: CmField) =>
        isFirstRowForPaintBlock ? formatFieldValue(field, row.cm[field.key], unitSystem) : ''
      ),
      formatAreaValue(getPsimValue(psimEntry, 'remediatedArea'), unitSystem),
      hasPsimData ? formatAreaValue(remediatedChaseArea, unitSystem) : '', // PSIM Chase Remediated Area
      formatAreaValue(actualTotalRemediatedArea, unitSystem),
      hasPsimData ? '' : formatAreaValue(remediatedChaseArea, unitSystem), // CM Remediated Chase Area
      formatAreaValue(totalAlreadyCompleted, unitSystem),
      ...PSIM_REMEDIATION_FIELDS.map((field) =>
        formatFieldValue(field, getPsimValue(row.remediation, field.key), unitSystem)
      ),
      ...CM_REMEDIATION_FIELDS.map((field: CmField) =>
        isFirstRowForPaintBlock ? formatFieldValue(field, row.cm[field.key], unitSystem) : ''
      ),
    ];

    const excelRow = worksheet.addRow(values);
    excelRow.eachCell((cell) => {
      cell.font = { name: 'Arial' };
      cell.alignment = { vertical: 'top', horizontal: 'left', wrapText: true };
    });
  });

  // Set column widths
  worksheet.getColumn(1).width = 18;
  worksheet.getColumn(2).width = 22;
  worksheet.getColumn(3).width = 20;
  for (let col = 4; col <= headers.length; col += 1) {
    worksheet.getColumn(col).width = 20;
  }

  return workbook;
};
